import { pick } from 'lodash-es';
import { runTrackedAudioWork, swallowAudioWorkCutoff } from '../../audioWorkBarrier';
import { type RecordingKey } from '../../persistenceV2/types';
import { type SessionChunk } from '../../types';
import { useAudioGraphStore } from '../../useAudioGraphStore';
import { getChunkInfo } from '../lib/getChunkInfo';
import { logger } from '../lib/logger';
import { releaseAnalyzerId } from '../lib/releaseAnalyzerId';
import { ChunkCaptureRecorder, type CapturedChunk } from './ChunkCaptureRecorder';
import { discardReplacedCaptureSlot, stopCaptureSlot } from './captureSlotLifecycle';
import { handleCaptureEndedV2 } from './finalizeV2';
import { handleNewChunkV2 } from './handleNewChunkV2';
import { requireConfig, type GetState, type SetState } from './storeTypes';

/** Owns the single live v2 capture slot; uploads continue after capture ends. */

// Flush on pagehide only; committed chunks are recovered on the next load.
const FLUSH_MIN_BUFFERED_PAGEHIDE_MS = 1_000;

let pageHideFlushHandler: (() => void) | null = null;

const releaseSlotAnalyzers = (recorder: ChunkCaptureRecorder) => {
  const { releaseMicAnalyzer, releaseTabAnalyzer } = useAudioGraphStore.getState();
  releaseMicAnalyzer(recorder.micAnalyzerId);
  if (recorder.tabAnalyzerId) releaseTabAnalyzer(recorder.tabAnalyzerId);
};

const installPageHideFlush = (get: GetState) => {
  if (pageHideFlushHandler || typeof window === 'undefined') return;
  pageHideFlushHandler = () => {
    get().captureSlot?.recorder.flush(FLUSH_MIN_BUFFERED_PAGEHIDE_MS);
  };
  window.addEventListener('pagehide', pageHideFlushHandler);
};

/** Test-only. */
export const __resetPageHideFlushForTests = () => {
  if (pageHideFlushHandler && typeof window !== 'undefined') {
    window.removeEventListener('pagehide', pageHideFlushHandler);
  }
  pageHideFlushHandler = null;
};

export const startCaptureV2 = (
  set: SetState,
  get: GetState,
  stream: MediaStream,
  useTranscriptionServiceV2: boolean,
  recordingKey: RecordingKey
) => {
  const { sessionId, recordingId } = recordingKey;

  // Replace an unexpected stale recorder without leaking analyzers.
  discardReplacedCaptureSlot({
    slot: get().captureSlot,
    replacedBy: recordingId,
    eventType: 'CAPTURE_RECORDER_REPLACED',
    beforeDiscard: releaseSlotAnalyzers,
  });

  installPageHideFlush(get);

  const { getMicAnalyzer, getTabAnalyzer, releaseMicAnalyzer, releaseTabAnalyzer } =
    useAudioGraphStore.getState();
  const { id: micId, analyzer: micAnalyzer } = getMicAnalyzer();
  const { id: tabId, analyzer: tabAnalyzer } = getTabAnalyzer();

  const recorderProps: ConstructorParameters<typeof ChunkCaptureRecorder>[0] = {
    ...pick(requireConfig(get), ['chunkSpec', 'format', 'codec']),
    stream,
    micAnalyzer,
    micAnalyzerId: micId,
    tabAnalyzer,
    tabAnalyzerId: tabId,
    onChunk: (captured: CapturedChunk) => {
      const sessionChunk: SessionChunk = {
        sessionId,
        recordingId,
        isProcessed: false,
        wasMissingChunk: false,
        ...captured,
      };
      logger.debug({ event_type: 'CHUNK_ADDED', details: getChunkInfo(sessionChunk) });
      // Awaited by the recorder so stop() settles at the final persist; the
      // upload continues detached inside handleNewChunkV2.
      return runTrackedAudioWork(
        (context) =>
          handleNewChunkV2(get, sessionChunk, useTranscriptionServiceV2, context),
        'other'
      )
        .then(() => undefined)
        .catch(swallowAudioWorkCutoff);
    },
    onEnded: (lastIndex: number) => {
      void runTrackedAudioWork(
        (context) =>
          handleCaptureEndedV2(set, get, { sessionId, recordingId }, lastIndex, context),
        'other'
      );
    },
    onError: (errorReason: string) => {
      logger.error({
        event_type: 'CHUNK_RECORDER_FATAL_ERROR',
        recording_id: recordingId,
        details: errorReason,
      });
      get().config?.onInterrupt?.();
    },
  };
  let captureRecorder: ChunkCaptureRecorder;
  try {
    captureRecorder = new ChunkCaptureRecorder(recorderProps);
  } catch (err) {
    releaseAnalyzerId(releaseMicAnalyzer, micId);
    if (tabId) releaseAnalyzerId(releaseTabAnalyzer, tabId);
    throw err;
  }

  set({ captureSlot: { recordingId, recorder: captureRecorder } });
};

/** Stops capture once and releases its analyzers; persist-bound tail. */
export const stopCaptureV2 = (
  set: SetState,
  get: GetState,
  recordingId?: string
): Promise<void> => {
  const expectedId = recordingId ?? get().activeRecordingKey?.recordingId;
  return stopCaptureSlot({
    slot: get().captureSlot,
    expectedId,
    alreadyRetiredEvent: 'STOP_CHUNK_RECORDER_ALREADY_RETIRED',
    mismatchEvent: 'STOP_CHUNK_RECORDER_RECORDING_MISMATCH',
    beforeStop: releaseSlotAnalyzers,
    clear: () => set({ captureSlot: null }),
  });
};

/** Discards capture during logout/teardown without emitting more audio. */
export const discardCaptureSlotV2 = (set: SetState, get: GetState) => {
  const slot = get().captureSlot;
  if (!slot) return;
  releaseSlotAnalyzers(slot.recorder);
  slot.recorder.discard();
  set({ captureSlot: null });
};
